import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Briefcase,
  GraduationCap,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Mail,
  Phone,
  Calendar,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOBS = [
  {
    id: "nil-brand-partnerships",
    title: "NIL & Brand Partnerships Manager",
    type: "Full-Time",
    location: "Houston, TX",
    blurb:
      "Build and operate the AfroAm NIL pipeline. Manage athlete brand deals, sponsorship activations, and league-wide partnerships.",
  },
  {
    id: "legal-compliance",
    title: "Legal & Compliance Counsel",
    type: "Full-Time / Contract",
    location: "Remote · Houston, TX preferred",
    blurb:
      "Lead all legal, contractual, NIL, and compliance matters across AAMBL, WAABL, and the showcase circuit.",
  },
  {
    id: "intl-player-placement",
    title: "International Player Placement Coordinator",
    type: "Full-Time",
    location: "Houston, TX · International Travel",
    blurb:
      "Place AfroAm athletes with pro teams, agencies, and academies across Europe, Africa, Asia, and the Americas.",
  },
  {
    id: "tech-data-manager",
    title: "Technology & Data Manager",
    type: "Full-Time",
    location: "Houston, TX · Hybrid",
    blurb:
      "Own the AfroAm tech stack — analytics, scouting data, broadcasting infrastructure, and league ops platforms.",
  },
  {
    id: "marketing-comms",
    title: "Marketing & Communications Director",
    type: "Full-Time",
    location: "Houston, TX",
    blurb:
      "Shape the AfroAm voice across press, social, content, and live broadcast. Lead campaigns for showcases and season.",
  },
  {
    id: "brand-ambassador",
    title: "Brand Ambassador",
    type: "Part-Time / Contract",
    location: "Multiple Cities",
    blurb:
      "Represent AfroAm in markets across the US and abroad. Activate community programs, retail, and event campaigns.",
  },
  {
    id: "graphic-media",
    title: "Graphic Designer & Media Specialist",
    type: "Full-Time / Contract",
    location: "Houston, TX · Hybrid",
    blurb:
      "Drive AfroAm's visual identity — graphics, video, social cuts, in-arena assets, and printed campaigns.",
  },
];

const inputCls =
  "w-full bg-transparent border-b border-neutral-700 focus:border-amber-400 text-white py-3 outline-none placeholder:text-neutral-700 transition-colors text-sm";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-neutral-400";

const EmploymentPage = () => {
  const [form, setForm] = useState({
    position: "",
    track: "Full-Time Role",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    portfolio: "",
    resume_link: "",
    cover_letter: "",
    agree_to_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["position", "first_name", "last_name", "email", "phone"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please complete: ${k.replace("_", " ")}.`);
        return;
      }
    }
    if (!form.agree_to_terms) {
      toast.error("Please acknowledge the certification.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/employment`, form);
      toast.success("Application received. We'll be in touch.");
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (jobTitle, track = "Full-Time Role") => {
    setField("position", jobTitle);
    setField("track", track);
    setTimeout(() => {
      document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/12 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] pointer-events-none" />

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Briefcase size={14} />
            <span>Careers · AfroAm Front Office</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-testid="employment-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            Join our team.
            <br />
            <span className="text-amber-400">Build the legacy.</span>
          </motion.h1>
          <p className="text-neutral-400 max-w-2xl mt-6 font-light leading-relaxed">
            AfroAm Basketball Association is hiring driven professionals to
            help grow our global mission on and off the court — plus a
            year-round college internship program across operations, marketing,
            scouting, media, and community.
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <a href="#jobs" data-testid="jump-jobs" className="inline-flex items-center gap-2 bg-amber-400 text-black px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors">
              See Open Roles <ArrowRight size={14} />
            </a>
            <a href="#internships" data-testid="jump-internships" className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-300 px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-400 hover:text-black transition-colors">
              <GraduationCap size={14} /> College Internships
            </a>
            <a href="#apply" data-testid="jump-apply" className="inline-flex items-center gap-2 border border-white text-white px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors">
              Apply Now
            </a>
          </div>
        </div>
      </section>

      {/* Jobs */}
      <section id="jobs" className="py-16 md:py-24 border-b border-neutral-900">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
                — Open Positions
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
                Roles open <span className="text-amber-400">now.</span>
              </h2>
            </div>
            <p className="text-neutral-400 max-w-sm font-light leading-relaxed">
              Tap a role to start your application — we&apos;ll route it to the right
              executive on the board.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="jobs-grid">
            {JOBS.map((j, i) => (
              <motion.article
                key={j.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 4) * 0.06, duration: 0.5 }}
                data-testid={`job-${j.id}`}
                className="group relative border border-neutral-900 bg-[#0a0a0a] hover:border-amber-400/50 transition-colors p-6 md:p-7"
              >
                <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.35em] text-amber-400 mb-3">
                  <span className="w-6 h-px bg-amber-400" />
                  {String(i + 1).padStart(2, "0")} / {String(JOBS.length).padStart(2, "0")}
                </div>
                <h3 className="font-display uppercase text-2xl md:text-3xl text-white leading-tight">
                  {j.title}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-4 text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                  <span className="border border-neutral-800 px-2.5 py-1">{j.type}</span>
                  <span className="text-neutral-500">{j.location}</span>
                </div>
                <p className="text-sm text-neutral-400 font-light leading-relaxed mt-5">
                  {j.blurb}
                </p>
                <button
                  type="button"
                  onClick={() => handleApply(j.title, "Full-Time Role")}
                  data-testid={`apply-${j.id}`}
                  className="mt-6 inline-flex items-center gap-2 bg-amber-400 text-black px-5 py-3 text-[10px] uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  Apply <ArrowRight size={12} />
                </button>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Internships */}
      <section id="internships" className="py-16 md:py-24 border-b border-neutral-900 bg-[#070707]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
              — College Internships
            </div>
            <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
              Build with us
              <br />
              <span className="text-amber-400">from campus.</span>
            </h2>
            <p className="text-neutral-300 max-w-xl mt-6 font-light leading-relaxed">
              AfroAm&apos;s year-round internship program for undergraduates and
              recent graduates — rotations across operations, marketing,
              scouting, media, community programs, and league administration.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900 mt-8">
              <div className="bg-black p-5">
                <GraduationCap size={16} className="text-amber-400 mb-3" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Open To</div>
                <div className="text-sm text-white">Undergraduates & Recent Graduates</div>
              </div>
              <div className="bg-black p-5">
                <Calendar size={16} className="text-amber-400 mb-3" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Cohort</div>
                <div className="text-sm text-white">Fall → Summer rotations</div>
              </div>
              <div className="bg-black p-5">
                <Briefcase size={16} className="text-amber-400 mb-3" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Starts</div>
                <div className="text-sm text-white">August 1, 2026</div>
              </div>
            </div>

            <div className="mt-8 border border-neutral-800 bg-black p-6 md:p-7">
              <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400 mb-3">
                Resume Of Interest To
              </div>
              <div className="font-display uppercase text-2xl md:text-3xl text-white">
                Ms. ZaRaya Porter
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">
                College Internship Recruitment Director
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="mailto:afroambasketball@gmail.com?subject=College%20Internship%20Application%20-%20Resume%20Of%20Interest&body=Please%20send%20your%20resume,%20school,%20expected%20graduation,%20and%20area%20of%20interest."
                  data-testid="intern-email-cta"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  <Mail size={14} /> Email Resume
                </a>
                <button
                  type="button"
                  onClick={() => handleApply("College Internship", "College Internship")}
                  data-testid="intern-apply-cta"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  Apply Online <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-16 md:py-24 border-b border-neutral-900">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative border border-amber-400/40 bg-[#0a0a0a] p-8 md:p-12"
              data-testid="apply-success"
            >
              <CheckCircle2 size={42} className="text-amber-400 mb-6" />
              <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.95]">
                Application
                <br />
                <span className="text-amber-400">received.</span>
              </h2>
              <p className="text-neutral-300 mt-6 leading-relaxed max-w-2xl">
                Thanks for applying to{" "}
                <span className="text-white">{submitted.position}</span> ({submitted.track}).
                We&apos;ll review and reach out at{" "}
                <span className="text-white">{submitted.email}</span> within
                7–14 business days.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/"
                  data-testid="apply-back-home"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  Back to Home
                </Link>
              </div>
            </motion.div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
                — Apply
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9]">
                Submit your <span className="text-amber-400">application.</span>
              </h2>
              <p className="text-neutral-400 max-w-2xl mt-5 font-light leading-relaxed">
                Selected a role above? It auto-fills here. Otherwise, type in
                the title or &quot;College Internship&quot;.
              </p>

              <form onSubmit={submit} data-testid="employment-form" className="mt-10 space-y-7">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>Position Applying For *</label>
                    <input className={inputCls} value={form.position} onChange={(e) => setField("position", e.target.value)} data-testid="emp-position" required />
                  </div>
                  <div>
                    <label className={labelCls}>Track</label>
                    <select className={`${inputCls} appearance-none [color-scheme:dark]`} value={form.track} onChange={(e) => setField("track", e.target.value)} data-testid="emp-track">
                      <option>Full-Time Role</option>
                      <option>Part-Time / Contract</option>
                      <option>College Internship</option>
                      <option>Brand Ambassador</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>First Name *</label>
                    <input className={inputCls} value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} data-testid="emp-first" required />
                  </div>
                  <div>
                    <label className={labelCls}>Last Name *</label>
                    <input className={inputCls} value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} data-testid="emp-last" required />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input type="email" className={inputCls} value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="emp-email" required />
                  </div>
                  <div>
                    <label className={labelCls}>Phone *</label>
                    <input type="tel" className={inputCls} value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="emp-phone" required />
                  </div>
                  <div>
                    <label className={labelCls}>City / Location</label>
                    <input className={inputCls} value={form.location} onChange={(e) => setField("location", e.target.value)} data-testid="emp-location" />
                  </div>
                  <div>
                    <label className={labelCls}>LinkedIn Profile</label>
                    <input className={inputCls} value={form.linkedin} onChange={(e) => setField("linkedin", e.target.value)} data-testid="emp-linkedin" placeholder="https://linkedin.com/in/..." />
                  </div>
                  <div>
                    <label className={labelCls}>Portfolio / Website</label>
                    <input className={inputCls} value={form.portfolio} onChange={(e) => setField("portfolio", e.target.value)} data-testid="emp-portfolio" placeholder="https://" />
                  </div>
                  <div>
                    <label className={labelCls}>Resume Link (Drive / Dropbox / PDF)</label>
                    <input className={inputCls} value={form.resume_link} onChange={(e) => setField("resume_link", e.target.value)} data-testid="emp-resume" placeholder="https://" />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Cover Letter / Why AfroAm?</label>
                  <textarea rows={5} className={`${inputCls} resize-none`} value={form.cover_letter} onChange={(e) => setField("cover_letter", e.target.value)} data-testid="emp-cover" placeholder="Tell us why you're the right fit and what you'll bring to the AfroAm front office..." />
                </div>

                <div className="bg-[#0a0a0a] border border-neutral-900 p-4 text-xs text-neutral-400">
                  After submitting online, also email a full resume to{" "}
                  <a href="mailto:afroambasketball@gmail.com" className="text-amber-400 hover:text-amber-300">
                    afroambasketball@gmail.com
                  </a>{" "}
                  with the position title in the subject line.
                </div>

                <label className="flex items-start gap-3 text-xs text-neutral-300 leading-relaxed cursor-pointer" data-testid="emp-terms">
                  <input
                    type="checkbox"
                    checked={form.agree_to_terms}
                    onChange={(e) => setField("agree_to_terms", e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-amber-400"
                  />
                  <span>
                    I certify the information provided is accurate, and I
                    consent to AfroAm Basketball Association reviewing my
                    application and contacting me about this role.
                  </span>
                </label>

                <div className="pt-4 border-t border-neutral-900 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="emp-submit"
                    className="inline-flex items-center gap-3 bg-amber-400 text-black px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="animate-spin" size={14} /> : <ArrowRight size={14} />}
                    Submit Application
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    Reviewed by AfroAm Executive Board
                  </span>
                </div>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Contact strip */}
      <section className="py-12 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900">
          <a href="mailto:afroambasketball@gmail.com" className="bg-black p-6 hover:bg-[#0a0a0a] transition-colors flex items-center gap-4" data-testid="contact-email">
            <Mail size={18} className="text-amber-400" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Email</div>
              <div className="text-sm text-white">afroambasketball@gmail.com</div>
            </div>
          </a>
          <a href="tel:8323173208" className="bg-black p-6 hover:bg-[#0a0a0a] transition-colors flex items-center gap-4" data-testid="contact-phone">
            <Phone size={18} className="text-amber-400" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Phone</div>
              <div className="text-sm text-white">(832) 317-3208</div>
            </div>
          </a>
          <div className="bg-black p-6 flex items-start gap-4">
            <Building2 size={18} className="text-amber-400 mt-1 shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Home HQ</div>
              <div className="text-sm text-white">Las Vegas, Nevada</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-3">
                Southwest Office
              </div>
              <div className="text-sm text-white">
                708 Main St, Suite 1000, Houston, TX
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-3">
                Pacific Division Office
              </div>
              <div className="text-sm text-white">
                470 Noor St, Suite B, South San Francisco, CA
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-3">
                Web
              </div>
              <a
                href="https://afroamhoopsglobal.us"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white hover:text-amber-400"
              >
                afroamhoopsglobal.us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmploymentPage;
